import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

import {
	isEncouragingRereservationEnabled,
	isManuallyClaimingEnabled,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleEditV2/logic"
import {
	CancelFeeTypeForNonZeroCalculateKey,
	cancelFeeTypeForNonZeroYenReservationKey,
	cancelFeeTypeForNonZeroYenReservationKeyArray,
	maxFeeAmount,
	maxFeePercent,
	minFeeAmount,
	minFeePercent,
	selectableConversionDaysByManuallyClaimingUnitKind,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleV2/CompanySmsSendingRuleV2.const"
import {
	CancelPolicyTemplateType,
	CancelType,
	ManuallyClaimingUnitKind,
} from "@/souhl_admin/gql/__generated__/graphql"

type CancelTypeSettingEditSchemaValue = {
	cancelFeeTypeForNonZeroYenReservation?: CancelFeeTypeForNonZeroCalculateKey | null
	cancelFeeAmountForNonZeroYenReservation?: number | null
	cancelFeePercentForNonZeroYenReservation?: number | null
} & Record<string, unknown>

const normalizeOptionalNumberInput = (
	value: number | null | undefined,
	originalValue: unknown
): number | null | undefined =>
	Number.isNaN(originalValue) ? undefined : value

const normalizeRequiredNumberInput = (
	value: number | undefined,
	originalValue: unknown
): number => (Number.isNaN(originalValue) ? 0 : (value ?? 0))

// unit のバリデーション対象になる再予約案内区間があるか (親 setting の来店回数から判定)
const hasEncouragingRereservationRange = (parent: unknown): boolean => {
	const setting = parent as {
		minVisitCountForEncouragingRereservation?: number
		minVisitCountForNothing?: number
	} | null
	const start = setting?.minVisitCountForEncouragingRereservation
	const end = setting?.minVisitCountForNothing
	if (typeof start !== "number" || typeof end !== "number") return true
	return isEncouragingRereservationEnabled(start, end)
}

// unit のバリデーション対象になる手動請求区間があるか (親 setting の来店回数から判定)
const hasManuallyClaimingRange = (parent: unknown): boolean => {
	const setting = parent as {
		minVisitCountForManuallyClaimingCancelFee?: number
		minVisitCountForEncouragingRereservation?: number
	} | null
	const start = setting?.minVisitCountForManuallyClaimingCancelFee
	const end = setting?.minVisitCountForEncouragingRereservation
	if (typeof start !== "number" || typeof end !== "number") return true
	return isManuallyClaimingEnabled(start, end)
}

// 編集モーダル用の軽量スキーマ（更新に必要な項目のみに限定）
const cancelTypeSettingEditSchema = yup
	.object({
		id: yup.string().default(""),
		cancelType: yup.mixed<CancelType>().required(),
		cancelFeeTypeForNonZeroYenReservation: yup
			.mixed<CancelFeeTypeForNonZeroCalculateKey>()
			.oneOf(cancelFeeTypeForNonZeroYenReservationKeyArray)
			.nullable(), // NOTE: one of は 必須にできない?
		// 予約金額ありの請求は％か金額のどちらか一方
		cancelFeePercentForNonZeroYenReservation: yup
			.number()
			.nullable()
			.transform(normalizeOptionalNumberInput)
			.when("minVisitCountForEncouragingRereservation", {
				is: (val: number) => val >= 1, //  NOTE: すなわち請求機能を利用する場合
				then: (schema) =>
					schema
						.min(minFeePercent, `${minFeePercent}以上の値を入力してください`)
						.max(maxFeePercent, `${maxFeePercent}以下の値を入力してください`),
				otherwise: (schema) => schema.nullable(),
			}),
		cancelFeeAmountForNonZeroYenReservation: yup
			.number()
			.nullable()
			.transform(normalizeOptionalNumberInput)
			.when("minVisitCountForEncouragingRereservation", {
				is: (val: number) => val >= 1, //  NOTE: すなわち請求機能を利用する場合
				then: (schema) =>
					schema
						.min(minFeeAmount, `${minFeeAmount}以上の値を入力してください`)
						.max(maxFeeAmount, `${maxFeeAmount}以下の値を入力してください`),
				otherwise: (schema) => schema.nullable(),
			}),

		// 予約金額未定の請求金額は、請求機能を使用する場合のみ必須
		cancelFeeAmountForZeroYenReservation: yup
			.number()
			.min(0, `${0}以上の値を入力してください`)
			.max(maxFeeAmount, `${maxFeeAmount}以下の値を入力してください`)
			.required("必須項目です")
			.transform(normalizeRequiredNumberInput),

		// 任意
		claimingConversionPolicyName: yup.string().nullable().not([undefined]),
		encouragingRereservationPolicyName: yup
			.string()
			.nullable()
			.not([undefined]),

		// 再予約案内の来店回数区間ごとの送付種別 (0 件 = 上の再予約案内種別を使用)
		encouragingRereservationUnits: yup
			.array()
			.of(
				yup.object({
					minVisitCount: yup
						.number()
						.min(0)
						.max(20)
						.required("必須項目です")
						.transform(normalizeRequiredNumberInput),
					encouragingRereservationPolicyName: yup
						.string()
						.required("送付種別を選択してください"),
				})
			)
			.optional()
			.test(
				"unitsAscending",
				"再予約案内の来店回数区間は重複なく昇順で入力してください",
				function (units) {
					if (!units || units.length === 0) return true
					// 再予約案内区間が無い場合 unit は送信時に落とされる (helpers.ts) のでチェックしない
					if (!hasEncouragingRereservationRange(this.parent)) return true
					for (let i = 1; i < units.length; i++) {
						const prev = units[i - 1]?.minVisitCount
						const current = units[i]?.minVisitCount
						if (prev == null || current == null) continue
						if (current <= prev) return false
					}
					return true
				}
			)
			.test(
				"unitsWithinRange",
				"再予約案内の来店回数区間は「使用機能なし」の開始未満で入力してください",
				function (units) {
					if (!units || units.length === 0) return true
					if (!hasEncouragingRereservationRange(this.parent)) return true
					const minVisitCountForNothing = (
						this.parent as {
							minVisitCountForNothing?: number
						}
					)?.minVisitCountForNothing
					if (typeof minVisitCountForNothing !== "number") return true
					// 20 は「上限なし」(backend では null 扱い) なので単項の max(20) チェックに任せる
					if (minVisitCountForNothing >= 20) return true
					for (const unit of units) {
						if (unit?.minVisitCount == null) continue
						if (unit.minVisitCount >= minVisitCountForNothing) return false
					}
					return true
				}
			),

		// 手動請求の来店回数区間ごとの転換設定 (0 件 = 転換しない普通の手動請求)
		manuallyClaimingUnits: yup
			.array()
			.of(
				yup.object({
					minVisitCount: yup
						.number()
						.min(0)
						.max(20)
						.required("必須項目です")
						.transform(normalizeRequiredNumberInput),
					// null = 転換しない
					manuallyClaimingUnitKind: yup
						.mixed<ManuallyClaimingUnitKind>()
						.nullable()
						.defined(),
					// 種別と常にセット: 転換する unit では選べる日数のどれかが必須
					conversionDays: yup
						.number()
						.nullable()
						.defined()
						.test(
							"conversionDaysSelectable",
							"転換日数を選択してください",
							function (days) {
								const kind = (
									this.parent as {
										manuallyClaimingUnitKind?: ManuallyClaimingUnitKind | null
									}
								)?.manuallyClaimingUnitKind
								if (kind == null) return true
								if (days == null) return false
								return selectableConversionDaysByManuallyClaimingUnitKind[
									kind
								].includes(days)
							}
						),
				})
			)
			.optional()
			.test(
				"manuallyClaimingUnitsAscending",
				"手動請求の来店回数区間は重複なく昇順で入力してください",
				function (units) {
					if (!units || units.length === 0) return true
					// 手動請求区間が無い場合 unit は送信時に落とされる (helpers.ts) のでチェックしない
					if (!hasManuallyClaimingRange(this.parent)) return true
					for (let i = 1; i < units.length; i++) {
						const prev = units[i - 1]?.minVisitCount
						const current = units[i]?.minVisitCount
						if (prev == null || current == null) continue
						if (current <= prev) return false
					}
					return true
				}
			)
			.test(
				"manuallyClaimingUnitsWithinRange",
				"手動請求の来店回数区間は「再予約案内」の開始未満で入力してください",
				function (units) {
					if (!units || units.length === 0) return true
					if (!hasManuallyClaimingRange(this.parent)) return true
					const minVisitCountForEncouragingRereservation = (
						this.parent as {
							minVisitCountForEncouragingRereservation?: number
						}
					)?.minVisitCountForEncouragingRereservation
					if (typeof minVisitCountForEncouragingRereservation !== "number")
						return true
					// 20 は「上限なし」(backend では null 扱い) なので単項の max(20) チェックに任せる
					if (minVisitCountForEncouragingRereservation >= 20) return true
					for (const unit of units) {
						if (unit?.minVisitCount == null) continue
						if (unit.minVisitCount >= minVisitCountForEncouragingRereservation)
							return false
					}
					return true
				}
			),

		// 来店回数（0..20）
		minVisitCountForClaimingConversion: yup.number().min(0).max(20).required(),
		minVisitCountForEncouragingRereservation: yup
			.number()
			.min(0)
			.max(20)
			.required(),
		minVisitCountForManuallyClaimingCancelFee: yup
			.number()
			.min(0)
			.max(20)
			.required(),
		minVisitCountForNothing: yup.number().min(0).max(20).required(),
	})
	// 検証直前に、選択されていない側の値をnullへ正規化してペア衝突を避ける
	.transform((current: CancelTypeSettingEditSchemaValue | undefined) => {
		if (!current) return current
		const type = current.cancelFeeTypeForNonZeroYenReservation
		if (type === cancelFeeTypeForNonZeroYenReservationKey.PERCENT) {
			return {
				...current,
				cancelFeeAmountForNonZeroYenReservation: null,
			}
		}
		if (type === cancelFeeTypeForNonZeroYenReservationKey.AMOUNT) {
			return {
				...current,
				cancelFeePercentForNonZeroYenReservation: null,
			}
		}
		return current
	})
	.test(
		"nonZeroYenFeePair",
		"予約金額ありの請求は％または金額のどちらか一方を指定してください",
		(values) => {
			if (!values) return true
			const percent = values.cancelFeePercentForNonZeroYenReservation
			const amount = values.cancelFeeAmountForNonZeroYenReservation
			if (percent == null && amount == null) return true
			if (percent != null && amount != null) return false
			return true
		}
	)

export type CompanySmsSendingRuleV2EditCancelTypeSetting = yup.InferType<
	typeof cancelTypeSettingEditSchema
>

export const companySmsSendingRuleV2EditSchema = yup.object({
	id: yup.string().default(""),
	name: yup.string().required("配信ルール名は必須です"),
	cancelPolicyTemplateType: yup
		.mixed<CancelPolicyTemplateType>()
		.required()
		.default("ZERO_ONE"),
	cancelTypeSettingInputs: yup
		.array()
		.of(cancelTypeSettingEditSchema)
		.required(),
})

export type CompanySmsSendingRuleV2EditType = yup.InferType<
	typeof companySmsSendingRuleV2EditSchema
>

export const companySmsSendingRuleV2EditResolver = yupResolver(
	companySmsSendingRuleV2EditSchema
)
